// ORBAT-velger (UI): plasser en enhet fra styrkestrukturen på kartet.
//
// Dette er DOM-laget over den rene modellen i `orbat-velger.ts` — søk, filter og
// katalog bor der og er testet i node. Her er det bare skjerm: felt, liste, klikk.
//
// Hvorfor den finnes: enhetspaletten (`unit-palette.ts`) lager LØSE markeringer.
// Den velger et symbol, ikke en enhet — den plasserte enheten får id `u<n>` og er
// ikke noe sted i styrkestrukturen. Da kan ingenting nedstrøms slå opp enheten:
// ordre vet ikke hvilket NIVÅ den gjelder (og kan derfor ikke velge IRFMI framfor
// fempunktsordre, som GRUS krever for lag og tropp), og stridsevne kan ikke finne
// den. Velger man her, blir kartenhetens id ORBAT-unit-id-en — kartet og ORBAT
// snakker om samme enhet.
//
// Paletten beholdes ved siden av: en fiendtlig avdeling eller et løst observert
// kjøretøy står IKKE i vår egen ORBAT, og skal fortsatt kunne markeres.
import {
  byggOrbatKatalog, echelonAlternativer, lagOrbatPlassering, sokOrbat,
  type OrbatKatalogPost, type OrbatPlassering, type OrbatSokTreff,
} from './orbat-velger.js';
import type { OrbatEchelon, OrbatUnit } from '@skade/contracts';

/** Maks rader i lista. ORBAT har over 300 enheter; alle på én gang er ikke
 * lesbart, og lang liste gjør søkefeltet til det eneste brukbare verktøyet. */
export const MAKS_TREFF = 60;

export interface OrbatVelgerHooks {
  /** Styrkestrukturen NÅ (fra ORBAT-panelet, ikke seeden). */
  enheter: () => readonly OrbatUnit[];
  /** Id-ene som allerede står på kartet — markeres som plassert. */
  plasserte: () => Iterable<string>;
  /**
   * Operatøren valgte en enhet. `coord` er punktet den skal stå på når velgeren
   * ble åpnet fra høyreklikk; `null` betyr at kalleren skal ARMERE et kartklikk.
   */
  onVelg: (plassering: OrbatPlassering, coord: [number, number] | null) => void;
  /** Vis en enhet som allerede står på kartet (panorer + åpne kortet). */
  onVis?: (id: string) => void;
}

export class OrbatVelgerPanel {
  readonly el: HTMLElement;
  private open = false;
  private katalog: OrbatKatalogPost[] = [];
  private q = '';
  private echelon: OrbatEchelon | '' = '';
  private targetCoord: [number, number] | null = null;

  private sokEl!: HTMLInputElement;
  private echSel!: HTMLSelectElement;
  private listeEl!: HTMLElement;
  private notatEl!: HTMLElement;

  constructor(private hooks: OrbatVelgerHooks) {
    this.el = this.build();
    document.body.appendChild(this.el);
  }

  /** Armeringsmodus: velg enhet, klikk så på kartet. */
  toggle() {
    this.targetCoord = null;
    if (this.open) { this.close(); return; }
    this.vis();
  }

  /** Åpne mot et konkret punkt (høyreklikk «Plasser ORBAT-enhet her»). */
  openAt(lon: number, lat: number) {
    this.targetCoord = [lon, lat];
    this.vis();
  }

  close() {
    this.open = false;
    this.targetCoord = null;
    this.el.classList.remove('open');
  }

  private vis() {
    // Katalogen bygges ved HVER åpning, ikke i konstruktøren: ORBAT endres mens
    // appen står åpen (operatøren oppretter et lag, eller en annen operatørs
    // hendelse kommer inn via collab). En katalog fra oppstart ville vist en
    // struktur som ikke finnes lenger.
    this.katalog = byggOrbatKatalog(this.hooks.enheter());
    this.open = true;
    this.el.classList.add('open');
    this.byggEchelonValg();
    this.notatEl.textContent = this.targetCoord
      ? 'Enheten plasseres på punktet du høyreklikket.'
      : 'Velg enhet, og klikk deretter på kartet.';
    this.render();
    this.sokEl.focus();
    this.sokEl.select();
  }

  private byggEchelonValg() {
    const valgt = this.echSel.value;
    this.echSel.replaceChildren();
    const alle = document.createElement('option');
    alle.value = ''; alle.textContent = `Alle nivå (${this.katalog.length})`;
    this.echSel.append(alle);
    for (const a of echelonAlternativer(this.katalog)) {
      const o = document.createElement('option');
      o.value = a.echelon; o.textContent = `${a.navn} (${a.antall})`;
      this.echSel.append(o);
    }
    // Behold filteret over en ny åpning hvis nivået fortsatt finnes.
    this.echSel.value = [...this.echSel.options].some((o) => o.value === valgt) ? valgt : '';
    this.echelon = this.echSel.value as OrbatEchelon | '';
  }

  private render() {
    const plasserte = new Set(this.hooks.plasserte());
    const treff = sokOrbat(this.katalog, {
      q: this.q,
      echelon: this.echelon || undefined,
      plasserteIder: plasserte,
      maks: MAKS_TREFF,
    });

    this.listeEl.replaceChildren();
    if (!treff.length) {
      const tom = document.createElement('div');
      tom.className = 'orbatvelger-tom';
      tom.textContent = this.q
        ? `Ingen enhet i ORBAT matcher «${this.q}».`
        : 'Ingen enheter på dette nivået.';
      this.listeEl.append(tom);
      return;
    }
    for (const t of treff) this.listeEl.append(this.rad(t));
  }

  private rad(t: OrbatSokTreff): HTMLElement {
    const { post } = t;
    const rad = document.createElement('button');
    rad.className = 'orbatvelger-rad' + (t.erPlassert ? ' plassert' : '');
    rad.type = 'button';

    const kort = document.createElement('span');
    kort.className = 'orbatvelger-ech';
    kort.textContent = post.echelonKort;

    const midt = document.createElement('span');
    midt.className = 'orbatvelger-midt';
    const navn = document.createElement('span');
    navn.className = 'orbatvelger-navn';
    navn.textContent = post.kortNavn;
    const sti = document.createElement('span');
    sti.className = 'orbatvelger-sti';
    // Foreldrekjeden er det som skiller «Lag 1» fra «Lag 1» tre distrikt unna.
    sti.textContent = post.sti.length ? post.sti.join(' / ') : post.echelonNavn;
    midt.append(navn, sti);

    rad.append(kort, midt);

    if (t.erPlassert) {
      // ⚠ Én enhet, ett punkt. Å plassere en tropp som ALLEREDE står på kartet
      // ville overskrevet posisjonen dens uten at noen ba om det — og siden
      // id-en er den samme, ville det sett ut som om enheten hoppet. Raden blir
      // derfor en «vis meg hvor den står»-rad i stedet for en plasser-rad.
      const merke = document.createElement('span');
      merke.className = 'orbatvelger-merke';
      merke.textContent = 'på kartet';
      rad.append(merke);
      rad.title = `${post.navn} står allerede på kartet. Dra symbolet for å flytte den.`;
      rad.onclick = () => { this.close(); this.hooks.onVis?.(post.id); };
      return rad;
    }

    rad.title = post.stiTekst + (post.hq ? `\nStandkvarter: ${post.hq}` : '');
    rad.onclick = () => {
      const p = lagOrbatPlassering(post.id, this.targetCoord?.[0] ?? 0, this.targetCoord?.[1] ?? 0, this.katalog);
      if (!p) return; // katalogen er kilden; en rad uten post skal ikke kunne oppstå
      const coord = this.targetCoord;
      this.close();
      this.hooks.onVelg(p, coord);
    };
    return rad;
  }

  private build(): HTMLElement {
    const panel = document.createElement('div');
    panel.className = 'orbatvelger';

    const head = document.createElement('div');
    head.className = 'orbatvelger-head';
    const tittel = document.createElement('span');
    tittel.className = 'orbatvelger-title';
    tittel.textContent = 'Plasser enhet fra ORBAT';
    const x = document.createElement('button');
    x.className = 'orbatvelger-x'; x.textContent = '×';
    x.setAttribute('aria-label', 'Lukk');
    x.onclick = () => this.close();
    head.append(tittel, x);

    this.notatEl = document.createElement('div');
    this.notatEl.className = 'orbatvelger-note';

    const sok = document.createElement('input');
    sok.className = 'orbatvelger-sok';
    sok.type = 'search';
    sok.placeholder = 'Søk enhet, avdeling eller distrikt …';
    sok.setAttribute('aria-label', 'Søk i ORBAT');
    sok.oninput = () => { this.q = sok.value; this.render(); };
    sok.onkeydown = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); this.close(); }
      // Enter velger det øverste treffet: med hansker i mørket er det raskere å
      // skrive «oslo sen» + Enter enn å treffe en rad med pekeren.
      if (e.key === 'Enter') {
        e.preventDefault();
        const forste = this.listeEl.querySelector<HTMLButtonElement>('.orbatvelger-rad:not(.plassert)');
        forste?.click();
      }
    };
    this.sokEl = sok;

    const ech = document.createElement('select');
    ech.className = 'orbatvelger-ech-sel';
    ech.setAttribute('aria-label', 'Filtrer på nivå');
    ech.onchange = () => { this.echelon = ech.value as OrbatEchelon | ''; this.render(); };
    this.echSel = ech;

    const rad = document.createElement('div');
    rad.className = 'orbatvelger-filter';
    rad.append(sok, ech);

    this.listeEl = document.createElement('div');
    this.listeEl.className = 'orbatvelger-liste';

    panel.append(head, this.notatEl, rad, this.listeEl);
    return panel;
  }
}
